import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { FormGroup, FormControl, Validators } from '@angular/forms';

export interface Banco {
  codban: number;
  nomeBanco: string;
}

export interface Nacionalidade {
  codnac: number;
  desnac: string;
}

export interface Pais {
  codpai: number;
  nompai: string;
}

export interface Estado {
  codest: string;
  desest: string;
}

export interface Cidade {
  codcid: number;
  nomcid: string;
}

export interface Endereco {
  codbai: number;
  codcid: number;
  codest: string;
  nomrua: string;
}

export interface Bairro {
  codbai: number;
  nombai: string;
}

export interface Conhecimento {
  familia: string;
  conhecimento: string;
  descricao: string;
}

@Injectable({
  providedIn: 'root',
})
export class AutonomousService {
  constructor(private http: HttpClient) { }

  // snapshot dos ultimos dados do autonomo
  private currentAutonomous: any;

  private isPending = false;

  getBanks(): Observable<any> {
    return this.http.get('commons/bancos');
  }

  getContries(): Observable<any> {
    return this.http.get('commons/paises');
  }

  getStates(codpai: number): Observable<any> {
    return this.http.get(`commons/estados/${codpai}`);
  }

  getCities(codpai: number, codest: string): Observable<any> {
    return this.http.get(`commons/cidades/${codpai}/${codest}`);
  }

  checkAgency(body) {
    return this.http.post('commons/existe-agencia', body);
  }

  getAddress(cepCode): Observable<Endereco> {
    return this.http.get<Endereco>(`commons/cep/${cepCode}`);
  }

  getNeighborhoods(cityCode): Observable<Bairro[]> {
    return this.http.get<Bairro[]>(`commons/bairros/${cityCode}`);
  }

  getFamilies(): Observable<Conhecimento[]> {
    return this.http.get<Conhecimento[]>(`commons/familias`);
  }

  getExpertises(familia: string): Observable<Conhecimento[]> {
    return this.http.get<Conhecimento[]>(`commons/conhecimentos/${familia}`);
  }

  getLevelTraining(): Observable<any[]> {
    return this.http.get<any[]>(`commons/nivel-formacao`);
  }

  getSituacaoFormacacao(): Observable<any[]> {
    return this.http.get<any[]>(`commons/situacao-formacao`);
  }

  getTrainingCourses(nivelEscolaridade): Observable<any[]> {
    return this.http.get<any[]>(`commons/cursos-formacao/${nivelEscolaridade}`);
  }

  getTeachingInstitution(): Observable<any[]> {
    return this.http.get<any[]>(`commons/instituicao-ensino`);
  }

  getImprovementCourses(): Observable<any[]> {
    return this.http.get<any[]>(`commons/cursos-aperfeicoamento`);
  }

  getNacList(): Observable<any> {
    return this.http.get(`commons/nacionalidades`);
  }

  getAllAttachments(): Observable<any[]> {
    return this.http.get<any[]>(`files/`);
  }

  togglePending(){
    this.isPending = true;
  }

  toggleNotPending() {
    this.isPending = false;
  }

  getAutonomousData(): any {
    if (this.currentAutonomous != null) return this.currentAutonomous;
    return JSON.parse(sessionStorage.getItem('tabData'));
  }

  getAutonomousDataStorage(): any {
    return JSON.parse(sessionStorage.getItem('tabData'));
  }

  isFormPending(): boolean {
    return this.isPending;
  }

  clearAutonomousData(): any {
    this.currentAutonomous = null;
  }

  createCourse(id: number, course?): FormGroup {
    const form = new FormGroup({
      sequencia: new FormControl(id),
      curso: new FormControl(null, Validators.required),
      cursoRevisao: new FormControl(true),
      instituicao: new FormControl(null, Validators.required),
      instituicaoRevisao: new FormControl(true),
      dataInicio: new FormControl(null),
      dataInicioRevisao: new FormControl(true),
      dataTermino: new FormControl(null),
      dataTerminoRevisao: new FormControl(true),
      dataValidade: new FormControl(null),
      dataValidadeRevisao: new FormControl(true),
      situacao: new FormControl(null, Validators.required),
      situacaoRevisao: new FormControl(true),
      parecer: new FormControl(null, Validators.required),
    });
    if (course) { form.patchValue(course); }
    return form;
  }

  createAcademicTraining(id: number, academicTraning?): FormGroup {
    const form = new FormGroup({
      sequencia: new FormControl(id),
      nivelEscolaridade: new FormControl(null, Validators.required),
      nivelEscolaridadeRevisao: new FormControl(true),
      curso: new FormControl(null, Validators.required),
      cursoRevisao: new FormControl(true),
      instituicao: new FormControl(null, Validators.required),
      instituicaoRevisao: new FormControl(true),
      dataInicio: new FormControl(null, Validators.required),
      dataInicioRevisao: new FormControl(true),
      dataTermino: new FormControl(null, Validators.required),
      dataTerminoRevisao: new FormControl(true),
      situacao: new FormControl(null, Validators.required),
      situacaoRevisao: new FormControl(true),
      parecer: new FormControl(null, Validators.required),
    });
    if (academicTraning) { form.patchValue(academicTraning); }
    return form;
  }

  createInssRecolhido(id, inss?) {
    const form = new FormGroup({
      id: new FormControl(id),
      sequencia: new FormControl(null),
      valor: new FormControl(null),
      valorRevisao: new FormControl(true),
      competencia: new FormControl(null),
      tipoInscricao: new FormControl(null),
      tipoInscricaoRevisao: new FormControl(true),
      numeroInscricao: new FormControl(null),
      numeroInscricaoRevisao: new FormControl(true),
      cpfInscricao: new FormControl(null),
      cnpjInscricao: new FormControl(null),
      categoriaESocial: new FormControl(null),
      categoriaESocialRevisao: new FormControl(true),
      outraEmpresa: new FormControl(null),
      outraEmpresaRevisao: new FormControl(true),
      baseINSS: new FormControl(null),
      baseINSSRevisao: new FormControl(true),
      baseINSS13: new FormControl(null),
      baseINSS13Revisao: new FormControl(true),
      valorINSS13: new FormControl(null),
      valorINSS13Revisao: new FormControl(true),
      operacao: new FormControl(null)
    });
    if (inss) { form.patchValue(inss); }
    return form;
  }

  createDependent(dependent?) {
    const form = new FormGroup({
      nome: new FormControl(null, Validators.required),
      nomeRevisao: new FormControl(),
      nascimento: new FormControl(null, Validators.required),
      nascimentoRevisao: new FormControl(),
      sexo: new FormControl(null, Validators.required),
      sexoRevisao: new FormControl(),
      nomeMae: new FormControl(null, Validators.required),
      nomeMaeRevisao: new FormControl(),
      cpf: new FormControl(null, Validators.required),
      cpfRevisao: new FormControl(),
      parentesco: new FormControl(null, Validators.required),
      parentescoRevisao: new FormControl(),
      condicao: new FormControl(false),
    });
    if (dependent) { form.patchValue(dependent); }
    return form;
  }

  getTabData(tabName: string): Observable<any> {
    const sessionTabItem = JSON.parse(sessionStorage.getItem('tabData'));
    return of(sessionTabItem[tabName]);
  }

  postAutonomous(body: any): Observable<any[]> {
    return this.http.post<any[]>(`autonomo`, body);
  }

  updateCurrentStatus() {
    return this.http.patch<any>(`autonomo`, null);
  }

  setAutonomous(autonomous) {
    this.currentAutonomous = autonomous;
  }

  getChanges(actual) {
    const last = JSON.parse(sessionStorage.getItem('tabData'));
    this.removeConditionalBooleans(actual);
    let diff = Object.keys(actual).filter(k => {
      if (!Array.isArray(actual[k])) {
        return last[k] != actual[k];
      }
    });
    this.addArrayChanges(diff, 'cursosAperfeicoamento', actual, last);
    this.addArrayChanges(diff, 'formacoesAcademicas', actual, last);
    this.addArrayChanges(diff, 'inssRecolhidosTerceiro', actual, last);
    this.addArrayChanges(diff, 'dependentes', actual, last);
    return diff;
  }

  private removeConditionalBooleans(actual) {
    delete actual.exFuncionario;
    delete actual.estrangeiro;
  }

  private addArrayChanges(mainDiff, list, actual, last) {
    if (actual[list].length != last[list].length) {
      mainDiff.push(list);
      return;
    }

    for (let i = 0; i < actual[list].length; i++) {
      const currentActual = actual[list][i];
      const currentLast = last[list][i];
      let diffs = Object.keys(currentActual).filter(k => {
        if (typeof currentActual[k] === 'object' && currentActual[k] != null) {
          if (currentLast[k]) {
            if (currentActual[k].codigo) {
              return currentActual[k].codigo != currentLast[k].codigo;
            } else {
              return currentActual[k].nome != currentLast[k].nome;
            }
          }
        }
        if (k == 'sequencia') {
          return null;
        }
        if (!currentLast || !currentLast[k]) {
          return currentActual[k];
        }
        return currentActual[k] != currentLast[k];
      });

      if (diffs.length != 0) {
        mainDiff.push(list);
        break;
      }
    }
  }

}
